/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ViewUploadedFile.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/* global globalThis */
import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { HandleApiActions } from "../../redux/actions";
import testCasesActions from "../../redux/actions/testCasesActions";
import CustomDialogBoxV2 from "../customComponentsV2/common/customDialogBox/CustomDialogBoxV2";

const getFileExtension = (name = "") => {
  const trimmedName = String(name).trim();
  if (!trimmedName) return "";
  const lastDotIndex = trimmedName.lastIndexOf(".");
  if (lastDotIndex <= 0 || lastDotIndex === trimmedName.length - 1) {
    return "";
  }
  return trimmedName.slice(lastDotIndex);
};

function ViewUploadedFile({
  open,
  onClose,
  dialogTitle = "View Uploaded File",
  selectedFile,
  fileName,
  setSelectedFile,
  downloadFile = false,
}) {
  const dispatch = useDispatch();
  const [fileDataList, setFileDataList] = useState([]);
  const [loading, setLoading] = useState(false);
  const fileUrlsRef = useRef([]);
  const resolvedDownloadFileName =
    typeof fileName === "string" ? fileName.trim() : "";



  const buildDownloadFileName = useCallback((preferredName, sourceName) => {
    const safePreferredName = String(preferredName || "").trim();
    const safeSourceName = String(sourceName || "").trim();
    const preferredExtension = getFileExtension(safePreferredName);

    if (safePreferredName && preferredExtension) {
      return safePreferredName;
    }

    if (safePreferredName && safeSourceName) {
      const sourceExtension = getFileExtension(safeSourceName);
      if (sourceExtension) {
        return `${safePreferredName}${sourceExtension}`;
      }
    }

    return safePreferredName || safeSourceName || "downloaded-file";
  }, []);

  const resolveFileName = (file) => {
    if (!file) {
      return "";
    }

    if (typeof file === "string") {
      const trimmedFile = file.trim();
      if (!trimmedFile) {
        return "";
      }

      try {
        const parsedFile = JSON.parse(trimmedFile);
        if (parsedFile && typeof parsedFile === "object") {
          return parsedFile.filename || parsedFile.name || parsedFile.url || "";
        }
      } catch (error) {
        console.error("Error parsing JSON:", error);
      }

      return trimmedFile;
    }

    return file.filename || file.name || file.url || "";
  };

  const fetchFileBlob = useCallback(
    async (fileName) => {
      try {
        const resp = await Promise.resolve(dispatch(
          HandleApiActions({
            ...testCasesActions.get_files,
            params: {
              filename: fileName,
            },
            is_get_file: true,
          }),
        ));

        const url = globalThis.URL.createObjectURL(new Blob([resp]));
        return { fileName, url, error: false };
      } catch (error) {
        console.error("Error fetching file:", error);
        return { fileName, url: null, error: true };
      }
    },
    [dispatch],
  );

  const triggerBrowserDownload = useCallback((url, fileName) => {
    if (!url) return;

    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName || "downloaded-file";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }, []);

  useEffect(() => {
    let isActive = true;

    const revokeUrls = (urls) => {
      for (const url of urls || []) {
        if (url) {
          globalThis.URL.revokeObjectURL(url);
        }
      }
    };

    const loadFiles = async () => {
      setLoading(true);

      const resolvedFileName = resolveFileName(selectedFile);

      if (!resolvedFileName) {
        setFileDataList([{ fileName: null, url: null, error: true }]);
        setLoading(false);
        return;
      }

      const result = await fetchFileBlob(resolvedFileName);
      if (!isActive) {
        revokeUrls([result?.url]);
        return;
      }

      if (downloadFile) {
        triggerBrowserDownload(
          result?.url,
          buildDownloadFileName(resolvedDownloadFileName, resolvedFileName),
        );
        revokeUrls([result?.url]);
        fileUrlsRef.current = [];
        setFileDataList([]);
        setLoading(false);
        if (setSelectedFile) {
          setSelectedFile(null);
        }
        if (onClose) {
          onClose();
        }
        return;
      }

      revokeUrls(fileUrlsRef.current);
      fileUrlsRef.current = result?.url ? [result.url] : [];
      setFileDataList([result]);
      setLoading(false);
    };

    if (open && selectedFile) {
      loadFiles();
    } else {
      revokeUrls(fileUrlsRef.current);
      fileUrlsRef.current = [];
      setFileDataList([]);
      setLoading(false);
    }

    return () => {
      isActive = false;
      revokeUrls(fileUrlsRef.current);
      fileUrlsRef.current = [];
    };
  }, [
    open,
    selectedFile,
    fetchFileBlob,
    downloadFile,
    resolvedDownloadFileName,
    triggerBrowserDownload,
    onClose,
    setSelectedFile,
    buildDownloadFileName,
  ]);

  const handleClose = () => {
    onClose();
    if (setSelectedFile) {
      setSelectedFile(null);
    }
  };

  if (downloadFile) {
    return null;
  }

  return (
    <CustomDialogBoxV2 open={open} onClose={handleClose} title={dialogTitle}>
      <Box sx={{ width: "600px", maxHeight: "80vh", overflowY: "auto" }}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height={400}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={4}>
            {fileDataList.map(({ fileName, url, error }) => (
              <Box
                key={fileName || "uploaded-file"}
                border={1}
                borderColor="grey.300"
                borderRadius={2}
                p={2}
                overflow="hidden"
              >
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ wordBreak: "break-word", fontWeight: 500 }}
                >
                  {selectedFile?.name || fileName || "Uploaded File"}
                </Typography>
                {error || !url ? (
                  <Typography color="error">Unable to load image</Typography>
                ) : (
                  <Box
                    component="img"
                    src={url}
                    alt={fileName || selectedFile?.name || "Uploaded file"}
                    sx={{
                      p: 2,
                      width: "100%",
                      maxHeight: 400,
                      objectFit: "contain",
                      borderRadius: 1,
                      border: "1px solid #ddd",
                      backgroundColor: "#f9f9f9",
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </CustomDialogBoxV2>
  );
}

ViewUploadedFile.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  dialogTitle: PropTypes.string,
  selectedFile: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      name: PropTypes.string,
      filename: PropTypes.string,
      url: PropTypes.string,
    }),
  ]),
  fileName: PropTypes.string,
  setSelectedFile: PropTypes.func,
  downloadFile: PropTypes.bool,
};

export default ViewUploadedFile;
